import type { GetServerSideProps } from "next";
import { FormEvent, ReactElement, useState } from "react";

import Head from "next/head";
import NextLink from "next/link";

import {
  Alert,
  AlertIcon,
  Button,
  Container,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";

import { Auth, withSSRContext } from "aws-amplify";
import MainIndex from "components/layout/main-index";
import { useRouter } from "next/router";
import { HiOutlineEye, HiOutlineEyeOff } from "react-icons/hi";
import { useParameterStore } from "utils/context";

interface SignInState {
  email: string;
  password: string;
}

interface SignInStatusState {
  status: "success" | "error" | "info";
  message?: string;
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { Auth: AuthSSR } = withSSRContext(context);

  try {
    const userData = await AuthSSR.currentAuthenticatedUser();
    const { username } = userData;
    if (username) {
      return {
        redirect: {
          permanent: false,
          destination: "/",
        },
      };
    } else {
      return {
        props: {},
      };
    }
  } catch (error) {
    return {
      props: {},
    };
  }
};

const SignInPage = () => {
  const router = useRouter();
  const { returnUrl } = router.query;
  const { refreshUserProfile } = useParameterStore();

  const [signInData, setSignInData] = useState<SignInState>({
    email: "",
    password: "",
  });
  const [signInStatus, setSignInStatus] = useState<
    SignInStatusState | undefined
  >();
  const [showPass, setShowPass] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const signIn = async (e: FormEvent) => {
    e.preventDefault();

    if (signInData.email !== "" && signInData.password !== "") {
      setLoading(true);
      setSignInStatus({
        status: "info",
        message: "Trying to sign in, please wait.",
      });

      try {
        const user = await Auth.signIn(signInData.email, signInData.password);
        if (user) {
          setSignInStatus({
            status: "success",
            message: "Successfully sign in, redirecting to main page...",
          });

          refreshUserProfile();

          setTimeout(() => {
            if (returnUrl) {
              router.push(String(returnUrl));
            } else {
              router.push("/");
            }
          }, 1000);
        }
      } catch (error: any) {
        console.log(error);
        setLoading(false);
        setSignInData({ email: "", password: "" });
        setSignInStatus({
          status: "error",
          message: String(error.message),
        });
      }
    }
  };

  const handleInputChange = (event: any) => {
    setSignInData((s) => ({ ...s, [event.target.name]: event.target.value }));
  };

  return (
    <>
      <Container width={"full"} maxW={"100vw"} maxH={"90vh"}>
        <Flex
          justifyContent={"center"}
          alignItems={"center"}
          flexDirection={"column"}
          height={"90vh"}
          gap={8}
        >
          <Heading
            as={"h2"}
            size={"xl"}
            textTransform={"uppercase"}
            textAlign={"center"}
          >
            Sign In
          </Heading>
          <Container maxW={"container.sm"} p={6} rounded={"xl"}>
            <form onSubmit={(e) => signIn(e)}>
              <Stack gap={4}>
                <FormControl isRequired>
                  <FormLabel>Email address</FormLabel>
                  <Input
                    name={"email"}
                    id={"email"}
                    onChange={(e) => handleInputChange(e)}
                    value={signInData.email}
                    variant={"main"}
                    type={"email"}
                    placeholder={"Enter your valid email address"}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Password</FormLabel>
                  <InputGroup>
                    <Input
                      name={"password"}
                      id={"password"}
                      onChange={(e) => handleInputChange(e)}
                      value={signInData.password}
                      variant={"main"}
                      type={showPass ? "text" : "password"}
                      placeholder={"Enter your password"}
                    />
                    <InputRightElement pointerEvents={"auto"}>
                      <IconButton
                        display={"flex"}
                        aria-label="View Password"
                        variant={"unstyled"}
                        color={showPass ? "main.blue" : "gray.400"}
                        icon={showPass ? <HiOutlineEyeOff /> : <HiOutlineEye />}
                        fontSize={"md"}
                        size={"xs"}
                        onClick={() => setShowPass((s) => !s)}
                        cursor={"pointer"}
                      />
                    </InputRightElement>
                  </InputGroup>
                  <FormHelperText></FormHelperText>
                </FormControl>
                {signInStatus && (
                  <Alert status={signInStatus.status}>
                    <AlertIcon />
                    {signInStatus.message}
                  </Alert>
                )}
                <Button
                  type={"submit"}
                  variant={"gradient"}
                  isLoading={loading}
                >
                  Sign In
                </Button>
                <Flex
                  justifyContent={"space-between"}
                  flexDirection={{ base: "column", md: "row" }}
                  gap={2}
                >
                  <Text>
                    Don’t have an Account?{" "}
                    <Link as={NextLink} href={"/signup"} color={"main.magenta"}>
                      <strong>Sign Up</strong>
                    </Link>
                  </Text>
                  <Text>
                    <Link
                      as={NextLink}
                      href={"/password/reset"}
                      color={"main.magenta"}
                    >
                      <strong>Forget Password?</strong>
                    </Link>
                  </Text>
                </Flex>
                {/* <Flex gap={4} alignItems={"center"}>
                <Divider borderColor={"main.purple"} />
                <Text>OR</Text>
                <Divider borderColor={"main.purple"} />
              </Flex>
              <Flex gap={4} zIndex={0} alignItems={"center"}>
                <Button
                  variant={"gradient-outline"}
                  w={"full"}
                  leftIcon={<FaGoogle />}
                >
                  Sign Up with Google
                </Button>
                <Button
                  variant={"gradient-outline"}
                  w={"full"}
                  leftIcon={<FaFacebook />}
                >
                  Sign Up with Facebook
                </Button>
              </Flex> */}
              </Stack>
            </form>
          </Container>
        </Flex>
      </Container>
    </>
  );
};

SignInPage.getLayout = (page: ReactElement) => {
  return (
    <>
      <Head>
        <title>Signin — NFT One — Explore finest digital collection soon</title>
        <meta name="viewport" content="initial-scale=1.0, width=device-width" />
      </Head>
      <MainIndex>{page}</MainIndex>
    </>
  );
};

export default SignInPage;
